const MEBIBYTE = 1024 * 1024;

const CAD_EXPORT_ASSET_LIMITS = Object.freeze({
  maxConcurrentRequests: 4,
  requestTimeoutMs: 30_000,
  totalTimeoutMs: 120_000,
  maxItemBytes: 32 * MEBIBYTE,
  maxTotalBytes: 128 * MEBIBYTE,
  maxItems: 500
});

class CadExportAssetError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'CadExportAssetError';
    this.code = code;
  }
}

/**
 * Loads owner-bound Wireframe DXFs with a bounded, owner-fair request queue.
 * Results retain input order so callers can report individual failures without
 * coupling otherwise independent system contributions.
 */
export async function loadOwnedWireframeDxfAssets(items, options = {}) {
  const source = Array.isArray(items) ? items : [];
  const limits = effectiveLimits(options);
  // The browser fetch must keep the global object as receiver: it is called as a
  // property of the loader state below, which Chrome rejects ("Illegal invocation").
  const fetchImpl = typeof options.fetchImpl === 'function' ? options.fetchImpl
    : typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : null;
  if (typeof fetchImpl !== 'function') {
    throw new CadExportAssetError('CAD_ASSET_FETCH_UNAVAILABLE', 'Wireframe-Daten koennen nicht geladen werden.');
  }

  const results = new Array(source.length);
  const tasks = [];
  for (let index = 0; index < source.length; index += 1) {
    const item = source[index];
    const owner = typeof item?.owner === 'string' ? item.owner.trim() : '';
    const systemId = typeof item?.systemId === 'string' ? item.systemId.trim() : '';
    const path = typeof item?.wireframeDxfPath === 'string' ? item.wireframeDxfPath.trim() : '';
    const task = { index, item, owner, path };
    if (index >= limits.maxItems) {
      results[index] = rejectedResult(task, cadAssetError(
        'CAD_ASSET_ITEM_LIMIT',
        `Maximal ${limits.maxItems} Wireframe-Dateien sind erlaubt.`
      ));
    } else if (!owner || owner !== systemId || !path) {
      results[index] = rejectedResult(task, cadAssetError(
        'CAD_ASSET_ITEM_INVALID',
        'Wireframe-Beitrag besitzt keinen gueltigen Owner oder Asset-Pfad.'
      ));
    } else {
      tasks.push(task);
    }
  }

  const queue = ownerRoundRobinTasks(tasks);
  const activeControllers = new Set();
  const startedAt = monotonicNow();
  const deadlineAt = startedAt + limits.totalTimeoutMs;
  const acceptedBudget = { bytes: 0 };
  let totalDeadlineExpired = false;
  const hasTotalDeadlineExpired = () => totalDeadlineExpired;
  const totalTimer = setTimeout(() => {
    totalDeadlineExpired = true;
    for (const controller of activeControllers) controller.abort();
  }, limits.totalTimeoutMs);

  const workerCount = Math.min(limits.maxConcurrentRequests, queue.length);
  let cursor = 0;
  const nextTask = () => {
    if (cursor >= queue.length || totalDeadlineExpired || monotonicNow() >= deadlineAt) return null;
    const task = queue[cursor];
    cursor += 1;
    return task;
  };

  try {
    await Promise.all(Array.from({ length: workerCount }, async () => {
      while (true) {
        const task = nextTask();
        if (!task) return;
        try {
          const loaded = await loadWireframeTask(task, {
            fetchImpl,
            limits,
            deadlineAt,
            activeControllers,
            totalDeadlineExpired: hasTotalDeadlineExpired,
            acceptedBudget
          });
          results[task.index] = Object.freeze({
            status: 'fulfilled',
            index: task.index,
            owner: task.owner,
            item: task.item,
            dxfText: loaded.text,
            byteLength: loaded.byteLength
          });
        } catch (error) {
          results[task.index] = rejectedResult(task, normalizeCadAssetError(error));
        }
      }
    }));
  } finally {
    clearTimeout(totalTimer);
    for (const controller of activeControllers) controller.abort();
  }

  for (let index = 0; index < results.length; index += 1) {
    if (results[index]) continue;
    const item = source[index];
    results[index] = rejectedResult({
      index,
      item,
      owner: typeof item?.owner === 'string' ? item.owner.trim() : ''
    }, cadAssetError(
      'CAD_ASSET_TOTAL_TIMEOUT',
      `Das Gesamtzeitlimit von ${limits.totalTimeoutMs} ms fuer Wireframe-Daten wurde erreicht.`
    ));
  }

  const frozenResults = Object.freeze(results);
  return Object.freeze({
    results: frozenResults,
    loadedCount: frozenResults.filter(result => result.status === 'fulfilled').length,
    failedCount: frozenResults.filter(result => result.status === 'rejected').length,
    totalBytes: acceptedBudget.bytes
  });
}

async function loadWireframeTask(task, state) {
  const remainingTotalMs = Math.max(0, state.deadlineAt - monotonicNow());
  if (state.totalDeadlineExpired() || remainingTotalMs <= 0) {
    throw cadAssetError('CAD_ASSET_TOTAL_TIMEOUT', 'Das Gesamtzeitlimit fuer Wireframe-Daten wurde erreicht.');
  }
  const timeoutIsTotal = remainingTotalMs <= state.limits.requestTimeoutMs;
  const timeoutMs = Math.max(1, Math.min(state.limits.requestTimeoutMs, remainingTotalMs));
  const controller = new AbortController();
  state.activeControllers.add(controller);
  let timedOut = false;
  let timer = null;

  const operation = Promise.resolve().then(async () => {
    const response = await state.fetchImpl(task.path, {
      method: 'GET',
      signal: controller.signal
    });
    if (!response?.ok) {
      const status = Number(response?.status) || 0;
      throw cadAssetError(
        'CAD_ASSET_HTTP_ERROR',
        `Wireframe-Daten von ${safeOwnerLabel(task.owner)} konnten nicht geladen werden${status ? ` (${status})` : ''}.`
      );
    }
    const bytes = await readBoundedResponse(response, {
      signal: controller.signal,
      maxBytes: state.limits.maxItemBytes,
      owner: task.owner
    });
    const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    assertMinimalDxf(text, task.owner);
    if (state.acceptedBudget.bytes + bytes.byteLength > state.limits.maxTotalBytes) {
      throw cadAssetError(
        'CAD_ASSET_TOTAL_SIZE_LIMIT',
        `Das Gesamtlimit von ${formatMebibytes(state.limits.maxTotalBytes)} MiB fuer Wireframe-Daten wurde erreicht.`
      );
    }
    state.acceptedBudget.bytes += bytes.byteLength;
    return { text, byteLength: bytes.byteLength };
  });

  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
      reject(cadAssetError(
        timeoutIsTotal ? 'CAD_ASSET_TOTAL_TIMEOUT' : 'CAD_ASSET_REQUEST_TIMEOUT',
        timeoutIsTotal
          ? 'Das Gesamtzeitlimit fuer Wireframe-Daten wurde erreicht.'
          : `Wireframe-Daten von ${safeOwnerLabel(task.owner)} haben das Zeitlimit ueberschritten.`
      ));
    }, timeoutMs);
  });

  try {
    return await Promise.race([operation, timeout]);
  } catch (error) {
    if (error?.name === 'AbortError') {
      if (state.totalDeadlineExpired() || timeoutIsTotal) {
        throw cadAssetError('CAD_ASSET_TOTAL_TIMEOUT', 'Das Gesamtzeitlimit fuer Wireframe-Daten wurde erreicht.');
      }
      if (timedOut) {
        throw cadAssetError(
          'CAD_ASSET_REQUEST_TIMEOUT',
          `Wireframe-Daten von ${safeOwnerLabel(task.owner)} haben das Zeitlimit ueberschritten.`
        );
      }
    }
    throw error;
  } finally {
    if (timer !== null) clearTimeout(timer);
    controller.abort();
    state.activeControllers.delete(controller);
  }
}

async function readBoundedResponse(response, options) {
  const declaredLength = contentLength(response);
  if (declaredLength !== null && declaredLength > options.maxBytes) {
    throw cadAssetError(
      'CAD_ASSET_ITEM_SIZE_LIMIT',
      `Wireframe-Daten von ${safeOwnerLabel(options.owner)} sind groesser als ${formatMebibytes(options.maxBytes)} MiB.`
    );
  }

  const reader = response.body?.getReader?.();
  if (!reader) {
    throw cadAssetError(
      'CAD_ASSET_STREAM_UNAVAILABLE',
      'Wireframe-Daten muessen als begrenzbarer Datenstrom geliefert werden.'
    );
  }
  const chunks = [];
  let byteLength = 0;
  const cancelReader = () => {
    Promise.resolve(reader.cancel?.()).catch(() => undefined);
  };
  options.signal.addEventListener('abort', cancelReader, { once: true });
  try {
    while (true) {
      if (options.signal.aborted) throw abortError();
      const { done, value } = await reader.read();
      if (done) break;
      if (!(value instanceof Uint8Array)) {
        throw cadAssetError('CAD_ASSET_STREAM_INVALID', 'Wireframe-Datenstrom enthaelt ungueltige Daten.');
      }
      byteLength += value.byteLength;
      if (byteLength > options.maxBytes) {
        throw cadAssetError(
          'CAD_ASSET_ITEM_SIZE_LIMIT',
          `Wireframe-Daten von ${safeOwnerLabel(options.owner)} sind groesser als ${formatMebibytes(options.maxBytes)} MiB.`
        );
      }
      chunks.push(value);
    }
  } finally {
    options.signal.removeEventListener('abort', cancelReader);
    if (options.signal.aborted) cancelReader();
  }

  const bytes = new Uint8Array(byteLength);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

function contentLength(response) {
  const raw = response?.headers?.get?.('content-length');
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const normalized = String(raw).trim();
  if (!/^[0-9]+$/.test(normalized)) {
    throw cadAssetError('CAD_ASSET_CONTENT_LENGTH_INVALID', 'Wireframe-Daten besitzen eine ungueltige Groessenangabe.');
  }
  const value = Number(normalized);
  if (!Number.isSafeInteger(value) || value < 0) {
    throw cadAssetError('CAD_ASSET_CONTENT_LENGTH_INVALID', 'Wireframe-Daten besitzen eine ungueltige Groessenangabe.');
  }
  return value;
}

function assertMinimalDxf(text, owner) {
  const sectionIndex = text.search(/\bSECTION\b/i);
  const entitiesIndex = text.search(/\bENTITIES\b/i);
  const eofIndex = text.search(/\bEOF\b/i);
  if (sectionIndex < 0 || entitiesIndex <= sectionIndex || eofIndex <= entitiesIndex) {
    throw cadAssetError(
      'CAD_ASSET_DXF_INVALID',
      `Wireframe-Daten von ${safeOwnerLabel(owner)} besitzen keine gueltige DXF-Grundstruktur.`
    );
  }
}

function ownerRoundRobinTasks(tasks) {
  const groups = new Map();
  for (const task of tasks) {
    if (!groups.has(task.owner)) groups.set(task.owner, []);
    groups.get(task.owner).push(task);
  }
  const owners = [...groups.keys()].sort();
  const cursors = new Map(owners.map(owner => [owner, 0]));
  const result = [];
  let remaining = tasks.length;
  while (remaining > 0) {
    for (const owner of owners) {
      const ownerTasks = groups.get(owner);
      const cursor = cursors.get(owner) || 0;
      if (cursor >= ownerTasks.length) continue;
      result.push(ownerTasks[cursor]);
      cursors.set(owner, cursor + 1);
      remaining -= 1;
    }
  }
  return result;
}

function effectiveLimits(options) {
  return Object.freeze({
    maxConcurrentRequests: boundedOption(
      options.maxConcurrentRequests,
      CAD_EXPORT_ASSET_LIMITS.maxConcurrentRequests,
      CAD_EXPORT_ASSET_LIMITS.maxConcurrentRequests
    ),
    requestTimeoutMs: boundedOption(
      options.requestTimeoutMs,
      CAD_EXPORT_ASSET_LIMITS.requestTimeoutMs,
      CAD_EXPORT_ASSET_LIMITS.requestTimeoutMs
    ),
    totalTimeoutMs: boundedOption(
      options.totalTimeoutMs,
      CAD_EXPORT_ASSET_LIMITS.totalTimeoutMs,
      CAD_EXPORT_ASSET_LIMITS.totalTimeoutMs
    ),
    maxItemBytes: boundedOption(
      options.maxItemBytes,
      CAD_EXPORT_ASSET_LIMITS.maxItemBytes,
      CAD_EXPORT_ASSET_LIMITS.maxItemBytes
    ),
    maxTotalBytes: boundedOption(
      options.maxTotalBytes,
      CAD_EXPORT_ASSET_LIMITS.maxTotalBytes,
      CAD_EXPORT_ASSET_LIMITS.maxTotalBytes
    ),
    maxItems: CAD_EXPORT_ASSET_LIMITS.maxItems
  });
}

function boundedOption(value, fallback, maximum) {
  const numeric = Number(value);
  if (!Number.isSafeInteger(numeric) || numeric <= 0) return fallback;
  return Math.min(maximum, numeric);
}

function rejectedResult(task, error) {
  return Object.freeze({
    status: 'rejected',
    index: task.index,
    owner: task.owner || '',
    item: task.item,
    reason: error
  });
}

function normalizeCadAssetError(error) {
  if (error instanceof CadExportAssetError) return Object.freeze(error);
  return Object.freeze(cadAssetError(
    'CAD_ASSET_LOAD_FAILED',
    typeof error?.message === 'string' && error.message.trim()
      ? error.message.trim().slice(0, 500)
      : 'Wireframe-Daten konnten nicht geladen werden.'
  ));
}

function cadAssetError(code, message) {
  return new CadExportAssetError(code, message);
}

function abortError() {
  const error = new Error('Aborted');
  error.name = 'AbortError';
  return error;
}

function safeOwnerLabel(value) {
  const normalized = String(value || '').trim().slice(0, 120);
  return normalized || 'unbekanntem System';
}

function formatMebibytes(bytes) {
  return Math.max(1, Math.floor(bytes / MEBIBYTE));
}

function monotonicNow() {
  return Number(globalThis.performance?.now?.()) || Date.now();
}

export { CAD_EXPORT_ASSET_LIMITS, CadExportAssetError };
